<template>
  <div class="container">
    <PostSkeleton v-if="!fanId || isPostFetching" />
    <Post v-else-if="!isPostFetching && post" :post="post" />
    <ErrorMessage
      v-else-if="!isPostFetching && !post"
      :title="t('errors.contentErrorTitle')"
      :subtitle="t('errors.contentErrorSubtitle')"
    />
  </div>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { usePostStore } from '@stores/post.store';
import { useFanStore } from '@stores/fan.store';
import { useI18n } from 'vue-i18n';
import { useTelemetryFetcher } from '@api/tracking.api';
import { useScreenOrientation } from '@vueuse/core';
import { isMobile } from '@basitcodeenv/vue3-device-detect';
import { useVideoPlayerStore } from '@stores/videoPlayer.store';

// Components
import PostSkeleton from '@modules/Post/PostSkeleton.vue';
import ErrorMessage from '@modules/ErrorMessage.vue';
import Post from '@/components/modules/Post/Post.vue';

// Composables
const route = useRoute();
const { t } = useI18n();

// Stores
const fanStore = useFanStore();
const postStore = usePostStore();

// Refs
const { fanId } = storeToRefs(fanStore);
const { postId, post, isPostFetching } = storeToRefs(postStore);

// Video player orientation handling
const videoPlayerStore = useVideoPlayerStore();
const { isSupported: isOrientationSupported, orientation } = useScreenOrientation();
const isDeviceOrientationLandscape = computed(() => orientation.value === 'landscape-primary');

watch(isDeviceOrientationLandscape, (value) => {
  if (!isMobile || !isOrientationSupported.value) return;

  if (value) {
    videoPlayerStore.enableFullscreenOnRotate();
  } else {
    videoPlayerStore.disableFullscreenOnRotate();
  }
});

watch(
  fanId,
  (newFanId) => {
    if (newFanId) postId.value = (route.params.id as string) || '';
  },
  { immediate: true },
);

watch(
  postId,
  async () => {
    if (postId.value) {
      try {
        await postStore.fetchPost(postId.value);

        if (!post.value || post.value.length === 0) {
          return;
        }

        useTelemetryFetcher().execute({
          metric: 'view-post',
          resource: window.location.href,
        });
      } catch (error) {
        console.error(error);
      }
    } else postStore.resetPost();
  },
  { immediate: true },
);
</script>
